import { HeapEvent } from 'config';

import type { PlanName } from './Plan';
import type CartProduct from './CartProduct';

type ProductsByMealType = {
  dinner?: CartProduct[];
  prepared?: CartProduct[];
  ready_to_eat?: CartProduct[];
  grocery?: CartProduct[];
  bundle?: CartProduct[];
  breakfast?: CartProduct[];
  lunch?: CartProduct[];
  snack?: CartProduct[];
  add_on?: CartProduct[];
  less_prep?: CartProduct[];
};

class MealTypes {
  static dinner = {
    singular: 'dinner',
    plural: 'dinners',
    narrative: 'dinner meal kits',
    label: 'dinner',
    description: 'Awesome recipes and the exact right amount of ingredients.',
    heapEvent: HeapEvent.ANCHOR_MEALKITS,
  };

  static rte_dinner = {
    singular: 'dinner',
    plural: 'dinners',
    narrative: 'ready to eat dinner',
    label: 'dinner',
    heapEvent: HeapEvent.ANCHOR_RTE_DINNER,
  };

  static prepared = {
    singular: 'prepared meal',
    plural: 'prepared meals',
    narrative: 'fresh prepared meals',
    label: 'prepared',
    description: 'Delicious fresh meals. Heat `em up - ready in less than 5 minutes.',
    heapEvent: HeapEvent.ANCHOR_FRESHPREPARED,
  };

  static ready_to_eat = {
    singular: 'ready to eat meal',
    plural: 'ready to eat meals',
    narrative: 'ready to eat meals',
    label: 'ready to eat',
    description: 'Delicious fresh meals. Heat `em up - ready in less than 5 minutes.',
    heapEvent: HeapEvent.ANCHOR_FRESHPREPARED,
  };

  static breakfast = {
    singular: 'breakfast',
    plural: 'breakfasts',
    narrative: 'breakfast meal kits',
    label: 'breakfast',
    heapEvent: HeapEvent.ANCHOR_BREAKFAST,
  };

  static rte_breakfast = {
    singular: 'breakfast',
    plural: 'breakfasts',
    narrative: 'ready to eat breakfast',
    label: 'breakfast',
    heapEvent: HeapEvent.ANCHOR_RTE_BREAKFAST,
  };

  static lunch = {
    singular: 'lunch',
    plural: 'lunches',
    narrative: 'lunch meal kits',
    label: 'lunch',
    heapEvent: HeapEvent.ANCHOR_LUNCH,
  };

  static rte_lunch = {
    singular: 'lunch',
    plural: 'lunches',
    narrative: 'ready to eat lunch',
    label: 'lunch',
    heapEvent: HeapEvent.ANCHOR_RTE_LUNCH,
  };

  static snack = {
    singular: 'plantry item',
    plural: 'plantry items',
    narrative: 'plantry',
    label: 'plantry',
    heapEvent: HeapEvent.ANCHOR_PLANTRY,
  };

  static add_on = {
    singular: 'plantry item',
    plural: 'plantry items',
    narrative: 'plantry',
    label: 'plantry',
    heapEvent: HeapEvent.ANCHOR_PLANTRY,
  };

  static grocery = {
    singular: 'grocery',
    plural: 'grocery items',
    narrative: 'grocery',
    label: 'grocery',
    heapEvent: HeapEvent.ANCHOR_PLANTRY,
  };

  static bundle = {
    singular: 'bundle',
    plural: 'bundles',
    narrative: 'bundles',
    label: 'bundles',
    heapEvent: HeapEvent.ANCHOR_BUNDLE,
  };

  static less_prep = {
    singular: 'less prep',
    plural: 'less prep items',
    narrative: 'less prep',
    label: 'less prep',
    heapEvent: HeapEvent.ANCHOR_LESS_PREP,
  };

  static programs = {
    singular: 'program',
    plural: 'programs',
    narrative: 'Your Program This Week',
    label: 'program',
    description: 'Plant-Based Jumpstart',
    heapEvent: HeapEvent.ANCHOR_PROGRAMS,
  };

  // The following functions return an empty ProductsByMealType object or an
  // array of MealType keys with the keys sorted in the correct display order
  // (prepared plans show prepared meals first).

  static keys(planName?: PlanName): string[] {
    if (planName === 'prepared_one_serving') {
      return ['prepared', 'dinner', 'breakfast', 'add_on', 'lunch', 'snack', 'ready_to_eat', 'less_prep', 'grocery', 'bundle'];
    }

    return ['dinner', 'prepared', 'breakfast', 'add_on', 'lunch', 'snack', 'ready_to_eat', 'less_prep', 'grocery', 'bundle'];
  }

  static getFilteredProductsByMealType(
    filter: string,
    subFilter: string,
    isRteDaypartFiltersEnabled: boolean,
  ): ProductsByMealType {
    if ((filter === 'view_all' || filter === 'meal_kits') && subFilter === 'less_prep') {
      return { less_prep: [] };
    }

    const viewAllDictionary = isRteDaypartFiltersEnabled
      ? {
        dinner: [],
        rte_dinner: [],
        lunch: [],
        rte_lunch: [],
        breakfast: [],
        rte_breakfast: [],
        grocery: [],
        bundle: [],
      }
      : {
        dinner: [],
        ready_to_eat: [],
        lunch: [],
        breakfast: [],
        grocery: [],
        bundle: [],
      };
    const readyToEatDictionary = isRteDaypartFiltersEnabled
      ? {
        rte_dinner: [],
        rte_lunch: [],
        rte_breakfast: [],
      }
      : { ready_to_eat: [] };

    const dictionary = {
      programs: {
        dinner: [],
        breakfast: [],
        lunch: [],
      },
      view_all: viewAllDictionary,
      meal_kits: {
        breakfast: [],
        dinner: [],
        lunch: [],
      },
      ready_to_eat: readyToEatDictionary,
      grocery: {
        grocery: [],
        bundle: [],
      },
    };

    // include RTE under each daypart subfilter if feature flag is enabled
    if (isRteDaypartFiltersEnabled && filter === 'view_all' && ['dinner', 'lunch', 'breakfast'].includes(subFilter)) {
      const readyToEatSubFilter = `rte_${subFilter}`;
      const result = {};
      result[subFilter] = dictionary[filter][subFilter];
      result[readyToEatSubFilter] = dictionary[filter][readyToEatSubFilter];
      return result;
    }
    // otherwise include RTE under dinner subfilter
    if (!isRteDaypartFiltersEnabled && filter === 'view_all' && subFilter === 'dinner') {
      const readyToEatSubFilter = 'ready_to_eat';
      const result = {};
      result[subFilter] = dictionary[filter][subFilter];
      result[readyToEatSubFilter] = dictionary[filter][readyToEatSubFilter];
      return result;
    }

    if (subFilter !== undefined && subFilter !== '') {
      const result = {};
      result[subFilter] = dictionary[filter][subFilter];
      return result;
    }
    return dictionary[filter];
  }

  static getEmptyProductsByMealType(planName: PlanName): ProductsByMealType {
    if (planName === 'prepared_one_serving') {
      return {
        prepared: [],
        dinner: [],
        breakfast: [],
        add_on: [],
        lunch: [],
        snack: [],
        ready_to_eat: [],
        grocery: [],
        bundle: [],
        less_prep: [],
      };
    }

    return {
      dinner: [],
      prepared: [],
      breakfast: [],
      add_on: [],
      lunch: [],
      snack: [],
      ready_to_eat: [],
      grocery: [],
      bundle: [],
      less_prep: [],
    };
  }

  static pluralize(mealKey: string, qty: number): string {
    return qty === 1 ? MealTypes[mealKey].singular : MealTypes[mealKey].plural;
  }
}

// Add narrativeKey to MealTypes, which is a version of narrative names with
// blank spaces replaced with underscores, so they can be used in url hashes
// (e.g., 'dinner meal kits' narrative to 'dinner_meal_kits' narrativeKey).
MealTypes.keys()
  .forEach((key) => {
    MealTypes[key].narrativeKey = MealTypes[key].narrative.replace(/\s/g, '_');
  });

export type { ProductsByMealType };
export default MealTypes;
